import { ProviderDriverKind, ProviderInstanceId } from "@t3tools/contracts";
import { ClaudeAI, Gemini, type Icon, OpenAI } from "../Icons";
import {
  AiSingaporeGlyph,
  AmazonGlyph,
  CloudflareGlyph,
  DeepSeekGlyph,
  IbmGlyph,
  MetaGlyph,
  MistralGlyph,
  type ModelTier,
  MoonshotGlyph,
  NvidiaGlyph,
  QwenGlyph,
  ZaiGlyph,
  makeMonogramIcon,
} from "./providerIconUtils";

/**
 * Curated static showcase catalog for the composer model picker.
 *
 * These entries are DISPLAY-ONLY preview rows. They mirror the real
 * Cloudflare Workers AI + AWS Bedrock catalogs (grouped by COMPANY, tiered
 * free/pay/super) so the picker looks rich before the multi-provider pipeline
 * is wired up. Preview rows render as locked, labelled as previews, and MUST
 * NOT be selectable — the real, configured models continue to flow through the
 * existing `modelOptionsByInstance` pipeline untouched.
 *
 * To keep them from ever colliding with a real configured instance, every
 * preview entry is keyed by an instance id prefixed with
 * `PREVIEW_INSTANCE_PREFIX`. The picker looks that prefix up to decide whether
 * a row is a locked preview.
 */

// NOTE: must satisfy the ProviderInstanceId regex ^[a-zA-Z][a-zA-Z0-9_-]*$ — no colon.
export const PREVIEW_INSTANCE_PREFIX = "morrow-preview-";

export function isPreviewInstanceId(instanceId: string): boolean {
  return instanceId.startsWith(PREVIEW_INSTANCE_PREFIX);
}

/** Small capability badges rendered under the model name. */
export type ModelCapability = "vision" | "tools" | "reasoning";

/** Chat models are user-picked; utility models run automatically in the bg. */
export type ModelKind = "chat" | "utility";

/** Backing platform for a preview group (drives the section sub-label). */
export type PreviewPlatform = "cloudflare" | "bedrock" | "vertex";

export type PreviewCatalogModel = {
  /** Model slug — the real provider model id. */
  slug: string;
  /** Full model name. */
  name: string;
  /** Optional shorter label. */
  shortName?: string;
  /** One-line muted description shown under the name. */
  description: string;
  /** Capability badges. */
  capabilities: ReadonlyArray<ModelCapability>;
  /** Context window label, e.g. "128K" or "1M". */
  context?: string;
  /** Pricing/access tier — omitted for utility (background) models. */
  tier?: ModelTier;
};

export type PreviewCatalogGroup = {
  /** Preview instance id (prefixed) — unique, never collides with real ids. */
  instanceId: ProviderInstanceId;
  /** Driver kind — kept for row plumbing (all previews are locked anyway). */
  driverKind: ProviderDriverKind;
  /** Company display name (section header). */
  displayName: string;
  /** Company brand / monogram icon for the section header + row footer. */
  icon: Icon;
  /**
   * Optional Simple Icons slug for the real brand logo (rail rendering). When
   * the slug 404s or is omitted the `icon` fallback is used instead.
   */
  simpleIcon?: string | undefined;
  /** Backing platform (Cloudflare Workers AI vs AWS Bedrock). */
  platform: PreviewPlatform;
  /** chat = main grouped list; utility = collapsed background section. */
  kind: ModelKind;
  models: ReadonlyArray<PreviewCatalogModel>;
};

function previewInstanceId(slug: string): ProviderInstanceId {
  return ProviderInstanceId.make(`${PREVIEW_INSTANCE_PREFIX}${slug}`);
}

const CF_KIND = ProviderDriverKind.make("cloudflare");
// Bedrock has no dedicated driver kind here; reuse a neutral one for plumbing.
const BEDROCK_KIND = ProviderDriverKind.make("bedrock");

// ---------------------------------------------------------------------------
// CHAT catalog — grouped BY COMPANY.
// ---------------------------------------------------------------------------

const CLOUDFLARE_CHAT_GROUPS: ReadonlyArray<PreviewCatalogGroup> = [
  {
    instanceId: previewInstanceId("cf-meta"),
    driverKind: CF_KIND,
    displayName: "Meta",
    icon: MetaGlyph,
    simpleIcon: "meta",
    platform: "cloudflare",
    kind: "chat",
    models: [
      {
        slug: "@cf/meta/llama-3.2-1b-instruct",
        name: "Llama 3.2 1B Instruct",
        shortName: "Llama 3.2 1B",
        description: "Tiny, ultra-fast open model for lightweight chat.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "@cf/meta/llama-3.2-3b-instruct",
        name: "Llama 3.2 3B Instruct",
        shortName: "Llama 3.2 3B",
        description: "Small open model balancing speed and quality.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "@cf/meta/llama-3.1-8b-instruct-fp8",
        name: "Llama 3.1 8B Instruct (FP8)",
        shortName: "Llama 3.1 8B",
        description: "Quantized 8B general chat model served at the edge.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "@cf/meta/llama-3.2-11b-vision-instruct",
        name: "Llama 3.2 11B Vision Instruct",
        shortName: "Llama 3.2 11B Vision",
        description: "Multimodal open model that reads images and text.",
        capabilities: ["vision", "tools"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "@cf/meta/llama-4-scout-17b-16e-instruct",
        name: "Llama 4 Scout 17B",
        shortName: "Llama 4 Scout",
        description: "Efficient open multimodal MoE with a huge context.",
        capabilities: ["vision", "tools"],
        context: "1M",
        tier: "pay",
      },
      {
        slug: "@cf/meta/llama-3.3-70b-instruct-fp8-fast",
        name: "Llama 3.3 70B Instruct (Fast)",
        shortName: "Llama 3.3 70B",
        description: "Flagship 70B open model tuned for fast reasoning.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "super",
      },
    ],
  },
  {
    instanceId: previewInstanceId("cf-openai"),
    driverKind: CF_KIND,
    displayName: "OpenAI",
    icon: OpenAI,
    // Simple Icons 404s on "openai" — keep the real bundled OpenAI SVG.
    platform: "cloudflare",
    kind: "chat",
    models: [
      {
        slug: "@cf/openai/gpt-oss-20b",
        name: "GPT-OSS 20B",
        shortName: "GPT-OSS 20B",
        description: "Open-weight GPT model for everyday chat and tools.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "@cf/openai/gpt-oss-120b",
        name: "GPT-OSS 120B",
        shortName: "GPT-OSS 120B",
        description: "Large open-weight GPT flagship for hard reasoning.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "free",
      },
    ],
  },
  {
    instanceId: previewInstanceId("cf-deepseek"),
    driverKind: CF_KIND,
    displayName: "DeepSeek",
    icon: DeepSeekGlyph,
    simpleIcon: "deepseek",
    platform: "cloudflare",
    kind: "chat",
    models: [
      {
        slug: "@cf/deepseek-ai/deepseek-r1-distill-qwen-32b",
        name: "DeepSeek R1 Distill Qwen 32B",
        shortName: "R1 Distill 32B",
        description: "Reasoning model distilled into a 32B Qwen backbone.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "@cf/deepseek-ai/deepseek-v4-flash-0731",
        name: "DeepSeek V4 Flash",
        shortName: "V4 Flash",
        description: "Fast MoE chat model for interactive, low-cost use.",
        capabilities: ["tools"],
        context: "128K",
        tier: "pay",
      },
      {
        slug: "@cf/deepseek-ai/deepseek-v4-pro-0813",
        name: "DeepSeek V4 Pro",
        shortName: "V4 Pro",
        description: "Flagship DeepSeek MoE for deep coding and reasoning.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "super",
      },
    ],
  },
  {
    instanceId: previewInstanceId("cf-google"),
    driverKind: CF_KIND,
    displayName: "Google",
    icon: Gemini,
    simpleIcon: "googlegemini",
    platform: "cloudflare",
    kind: "chat",
    models: [
      {
        slug: "@cf/google/gemma-2b-it-lora",
        name: "Gemma 2B IT (LoRA)",
        shortName: "Gemma 2B",
        description: "Compact open Gemma model with LoRA fine-tuning support.",
        capabilities: ["tools"],
        context: "8K",
        tier: "free",
      },
      {
        slug: "@cf/google/gemma-7b-it-lora",
        name: "Gemma 7B IT (LoRA)",
        shortName: "Gemma 7B",
        description: "Mid-size open Gemma model for general chat at the edge.",
        capabilities: ["tools"],
        context: "8K",
        tier: "free",
      },
      {
        slug: "@cf/google/gemma-4-26b-a4b-it",
        name: "Gemma 4 26B",
        shortName: "Gemma 4 26B",
        description: "Larger open Gemma MoE for stronger general reasoning.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "pay",
      },
    ],
  },
  {
    instanceId: previewInstanceId("cf-mistral"),
    driverKind: CF_KIND,
    displayName: "Mistral",
    icon: MistralGlyph,
    simpleIcon: "mistralai",
    platform: "cloudflare",
    kind: "chat",
    models: [
      {
        slug: "@cf/mistral/mistral-7b-instruct-v0.2-lora",
        name: "Mistral 7B Instruct v0.2 (LoRA)",
        shortName: "Mistral 7B",
        description: "Efficient open 7B model with LoRA fine-tuning support.",
        capabilities: ["tools"],
        context: "32K",
        tier: "free",
      },
      {
        slug: "@cf/mistralai/mistral-small-3.1-24b-instruct",
        name: "Mistral Small 3.1 24B",
        shortName: "Mistral Small 24B",
        description: "Balanced 24B model for capable everyday chat and tools.",
        capabilities: ["vision", "tools"],
        context: "128K",
        tier: "pay",
      },
    ],
  },
  {
    instanceId: previewInstanceId("cf-qwen"),
    driverKind: CF_KIND,
    displayName: "Qwen",
    icon: QwenGlyph,
    simpleIcon: "alibabadotcom",
    platform: "cloudflare",
    kind: "chat",
    models: [
      {
        slug: "@cf/qwen/qwen3-30b-a3b-fp8",
        name: "Qwen3 30B A3B (FP8)",
        shortName: "Qwen3 30B",
        description: "Quantized Qwen3 MoE for fast general chat at the edge.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "@cf/qwen/qwen2.5-coder-32b-instruct",
        name: "Qwen2.5 Coder 32B",
        shortName: "Qwen2.5 Coder",
        description: "Code-specialised open model for programming tasks.",
        capabilities: ["tools"],
        context: "128K",
        tier: "pay",
      },
      {
        slug: "@cf/qwen/qwq-32b",
        name: "QwQ 32B",
        shortName: "QwQ 32B",
        description: "Qwen reasoning model with visible chain-of-thought.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "pay",
      },
    ],
  },
  {
    instanceId: previewInstanceId("cf-moonshot"),
    driverKind: CF_KIND,
    displayName: "Moonshot AI",
    icon: MoonshotGlyph,
    platform: "cloudflare",
    kind: "chat",
    models: [
      {
        slug: "@cf/moonshotai/kimi-k2.6",
        name: "Kimi K2.6",
        shortName: "Kimi K2.6",
        description: "Large Moonshot MoE flagship for agentic reasoning.",
        capabilities: ["tools", "reasoning"],
        context: "256K",
        tier: "super",
      },
      {
        slug: "@cf/moonshotai/kimi-k2.7-code",
        name: "Kimi K2.7 Code",
        shortName: "Kimi K2.7 Code",
        description: "Coding-tuned Kimi flagship for large-repo agent work.",
        capabilities: ["tools", "reasoning"],
        context: "256K",
        tier: "super",
      },
    ],
  },
  {
    instanceId: previewInstanceId("cf-zai"),
    driverKind: CF_KIND,
    displayName: "Z.AI",
    icon: ZaiGlyph,
    platform: "cloudflare",
    kind: "chat",
    models: [
      {
        slug: "@cf/zai-org/glm-4.7-flash",
        name: "GLM 4.7 Flash",
        shortName: "GLM 4.7 Flash",
        description: "Fast, low-cost GLM model for interactive chat.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "@cf/zai-org/glm-5.2",
        name: "GLM 5.2",
        shortName: "GLM 5.2",
        description: "Flagship GLM model for strong reasoning and tool use.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "pay",
      },
    ],
  },
  {
    instanceId: previewInstanceId("cf-nvidia"),
    driverKind: CF_KIND,
    displayName: "NVIDIA",
    icon: NvidiaGlyph,
    simpleIcon: "nvidia",
    platform: "cloudflare",
    kind: "chat",
    models: [
      {
        slug: "@cf/nvidia/nemotron-3-120b-a12b",
        name: "Nemotron 3 120B",
        shortName: "Nemotron 3 120B",
        description: "Large NVIDIA MoE flagship for demanding reasoning.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "super",
      },
    ],
  },
  {
    instanceId: previewInstanceId("cf-ibm"),
    driverKind: CF_KIND,
    displayName: "IBM",
    icon: IbmGlyph,
    simpleIcon: "ibm",
    platform: "cloudflare",
    kind: "chat",
    models: [
      {
        slug: "@cf/ibm-granite/granite-4.0-h-micro",
        name: "Granite 4.0 H Micro",
        shortName: "Granite 4.0 Micro",
        description: "Tiny IBM Granite model for efficient enterprise chat.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
    ],
  },
  {
    instanceId: previewInstanceId("cf-aisingapore"),
    driverKind: CF_KIND,
    displayName: "AI Singapore",
    icon: AiSingaporeGlyph,
    platform: "cloudflare",
    kind: "chat",
    models: [
      {
        slug: "@cf/aisingapore/gemma-sea-lion-v4-27b-it",
        name: "Gemma SEA-LION v4 27B",
        shortName: "SEA-LION v4 27B",
        description: "SE-Asian-language Gemma model for regional chat.",
        capabilities: ["tools"],
        context: "128K",
        tier: "pay",
      },
    ],
  },
];

// Monogram glyphs for Bedrock providers without a dedicated brand glyph.
const MiniMaxGlyph: Icon = makeMonogramIcon("MM", "", { color: "#ff4d4f" });
const WriterGlyph: Icon = makeMonogramIcon("WR", "", { color: "#5a4be6" });
const XaiGlyph: Icon = makeMonogramIcon("xAI", "", { color: "#111111" });

const BEDROCK_CHAT_GROUPS: ReadonlyArray<PreviewCatalogGroup> = [
  {
    instanceId: previewInstanceId("bedrock-openai"),
    driverKind: BEDROCK_KIND,
    displayName: "OpenAI",
    icon: OpenAI,
    simpleIcon: "openai",
    platform: "bedrock",
    kind: "chat",
    // Bedrock Mantle OpenAI-compatible endpoint exposes bare model ids
    // (no `us.` prefix, no version suffix). Rows key on (instanceId, slug).
    models: [
      {
        slug: "openai.gpt-5.6-sol",
        name: "GPT-5.6 Sol",
        shortName: "GPT-5.6 Sol",
        description: "Frontier GPT-5.6 tuned for deep reasoning and agents.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "256K",
        tier: "super",
      },
      {
        slug: "openai.gpt-5.6-luna",
        name: "GPT-5.6 Luna",
        shortName: "GPT-5.6 Luna",
        description: "Frontier GPT-5.6 balanced for speed and quality.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "256K",
        tier: "super",
      },
      {
        slug: "openai.gpt-5.6-terra",
        name: "GPT-5.6 Terra",
        shortName: "GPT-5.6 Terra",
        description: "Frontier GPT-5.6 grounded for broad general tasks.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "256K",
        tier: "super",
      },
      {
        slug: "openai.gpt-5.5",
        name: "GPT-5.5",
        shortName: "GPT-5.5",
        description: "Capable multimodal GPT for coding, chat and tools.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "256K",
        tier: "pay",
      },
      {
        slug: "openai.gpt-5.4",
        name: "GPT-5.4",
        shortName: "GPT-5.4",
        description: "Efficient GPT for reasoning-heavy coding and chat.",
        capabilities: ["tools", "reasoning"],
        context: "256K",
        tier: "pay",
      },
      {
        slug: "openai.gpt-oss-120b",
        name: "GPT-OSS 120B",
        shortName: "GPT-OSS 120B",
        description: "Open-weight GPT for reasoning and tool use.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "pay",
      },
      {
        slug: "openai.gpt-oss-20b",
        name: "GPT-OSS 20B",
        shortName: "GPT-OSS 20B",
        description: "Compact open-weight GPT for fast reasoning and tools.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "free",
      },
    ],
  },
  {
    instanceId: previewInstanceId("bedrock-anthropic"),
    driverKind: BEDROCK_KIND,
    displayName: "Anthropic",
    icon: ClaudeAI,
    simpleIcon: "anthropic",
    platform: "bedrock",
    kind: "chat",
    models: [
      {
        slug: "anthropic.claude-opus-5",
        name: "Claude Opus 5",
        shortName: "Opus 5",
        description: "Most capable Claude for frontier coding and agents.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "200K",
        tier: "super",
      },
      {
        slug: "anthropic.claude-fable-5",
        name: "Claude Fable 5",
        shortName: "Fable 5",
        description: "Creative frontier Claude for writing and reasoning.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "200K",
        tier: "super",
      },
      {
        slug: "anthropic.claude-opus-4-8",
        name: "Claude Opus 4.8",
        shortName: "Opus 4.8",
        description: "Powerful Claude for complex coding and agents.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "200K",
        tier: "super",
      },
      {
        slug: "anthropic.claude-opus-4-7",
        name: "Claude Opus 4.7",
        shortName: "Opus 4.7",
        description: "Powerful Claude for complex coding and agents.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "200K",
        tier: "super",
      },
      {
        slug: "anthropic.claude-sonnet-5",
        name: "Claude Sonnet 5",
        shortName: "Sonnet 5",
        description: "Fast, balanced Claude for everyday coding and chat.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "200K",
        tier: "pay",
      },
      {
        slug: "anthropic.claude-haiku-4-5",
        name: "Claude Haiku 4.5",
        shortName: "Haiku 4.5",
        description: "Fastest Claude for lightweight coding and chat.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "200K",
        tier: "free",
      },
    ],
  },
  {
    instanceId: previewInstanceId("bedrock-xai"),
    driverKind: BEDROCK_KIND,
    displayName: "xAI",
    icon: XaiGlyph,
    simpleIcon: "x",
    platform: "bedrock",
    kind: "chat",
    models: [
      {
        slug: "xai.grok-4.3",
        name: "Grok 4.3",
        shortName: "Grok 4.3",
        description: "Frontier Grok for reasoning, coding and agents.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "256K",
        tier: "super",
      },
    ],
  },
  {
    instanceId: previewInstanceId("bedrock-google"),
    driverKind: BEDROCK_KIND,
    displayName: "Google",
    icon: Gemini,
    simpleIcon: "googlegemini",
    platform: "bedrock",
    kind: "chat",
    models: [
      {
        slug: "google.gemma-4-31b",
        name: "Gemma 4 31B",
        shortName: "Gemma 4 31B",
        description: "Open Google Gemma 4 for multimodal chat and tools.",
        capabilities: ["vision", "tools"],
        context: "128K",
        tier: "pay",
      },
      {
        slug: "google.gemma-4-26b-a4b",
        name: "Gemma 4 26B",
        shortName: "Gemma 4 26B",
        description: "Open Google Gemma 4 MoE for multimodal chat and tools.",
        capabilities: ["vision", "tools"],
        context: "128K",
        tier: "pay",
      },
      {
        slug: "google.gemma-3-27b-it",
        name: "Gemma 3 27B",
        shortName: "Gemma 3 27B",
        description: "Open Google Gemma 3 for multimodal chat and tools.",
        capabilities: ["vision", "tools"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "google.gemma-3-12b-it",
        name: "Gemma 3 12B",
        shortName: "Gemma 3 12B",
        description: "Compact open Google Gemma 3 for chat and tools.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "google.gemma-3-4b-it",
        name: "Gemma 3 4B",
        shortName: "Gemma 3 4B",
        description: "Small open Google Gemma 3 for fast chat and tools.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "google.gemma-4-e2b",
        name: "Gemma 4 E2B",
        shortName: "Gemma 4 E2B",
        description: "Tiny open Google Gemma 4 for lightweight chat and tools.",
        capabilities: ["tools"],
        context: "32K",
        tier: "free",
      },
    ],
  },
  {
    instanceId: previewInstanceId("bedrock-deepseek"),
    driverKind: BEDROCK_KIND,
    displayName: "DeepSeek",
    icon: DeepSeekGlyph,
    simpleIcon: "deepseek",
    platform: "bedrock",
    kind: "chat",
    models: [
      {
        slug: "deepseek.v3.2",
        name: "DeepSeek V3.2",
        shortName: "V3.2",
        description: "MorrowOS default — powers the agent for everyday tasks. Free.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "deepseek.v3.1",
        name: "DeepSeek V3.1",
        shortName: "V3.1",
        description: "Strong open DeepSeek for reasoning and tool use.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "pay",
      },
    ],
  },
  {
    instanceId: previewInstanceId("bedrock-qwen"),
    driverKind: BEDROCK_KIND,
    displayName: "Qwen",
    icon: QwenGlyph,
    simpleIcon: "qwen",
    platform: "bedrock",
    kind: "chat",
    models: [
      {
        slug: "qwen.qwen3-vl-235b-a22b-instruct",
        name: "Qwen3-VL 235B",
        shortName: "Qwen3-VL 235B",
        description: "Frontier Qwen3 vision-language MoE for multimodal agents.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "256K",
        tier: "super",
      },
      {
        slug: "qwen.qwen3-coder-480b-a35b-instruct",
        name: "Qwen3 Coder 480B",
        shortName: "Qwen3 Coder 480B",
        description: "Frontier Qwen3 coder MoE for agentic coding.",
        capabilities: ["tools", "reasoning"],
        context: "256K",
        tier: "super",
      },
      {
        slug: "qwen.qwen3-235b-a22b-2507",
        name: "Qwen3 235B",
        shortName: "Qwen3 235B",
        description: "Large open Qwen3 MoE for reasoning and tools.",
        capabilities: ["tools", "reasoning"],
        context: "256K",
        tier: "pay",
      },
      {
        slug: "qwen.qwen3-next-80b-a3b-instruct",
        name: "Qwen3 Next 80B",
        shortName: "Qwen3 Next 80B",
        description: "Efficient Qwen3 Next MoE for reasoning and tools.",
        capabilities: ["tools", "reasoning"],
        context: "256K",
        tier: "pay",
      },
      {
        slug: "qwen.qwen3-coder-next",
        name: "Qwen3 Coder Next",
        shortName: "Qwen3 Coder Next",
        description: "Next-gen Qwen3 coder for agentic coding and tools.",
        capabilities: ["tools", "reasoning"],
        context: "256K",
        tier: "pay",
      },
      {
        slug: "qwen.qwen3-coder-30b-a3b-instruct",
        name: "Qwen3 Coder 30B",
        shortName: "Qwen3 Coder 30B",
        description: "Compact Qwen3 coder for fast coding and tools.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "qwen.qwen3-32b",
        name: "Qwen3 32B",
        shortName: "Qwen3 32B",
        description: "Open Qwen3 for reasoning, chat and tools.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "free",
      },
    ],
  },
  {
    instanceId: previewInstanceId("bedrock-mistral"),
    driverKind: BEDROCK_KIND,
    displayName: "Mistral",
    icon: MistralGlyph,
    simpleIcon: "mistralai",
    platform: "bedrock",
    kind: "chat",
    models: [
      {
        slug: "mistral.mistral-large-3-675b-instruct",
        name: "Mistral Large 3",
        shortName: "Large 3",
        description: "Frontier Mistral Large for reasoning and tools.",
        capabilities: ["tools", "reasoning"],
        context: "256K",
        tier: "super",
      },
      {
        slug: "mistral.devstral-2-123b",
        name: "Devstral 2 123B",
        shortName: "Devstral 2",
        description: "Mistral Devstral for agentic coding and tools.",
        capabilities: ["tools", "reasoning"],
        context: "256K",
        tier: "pay",
      },
      {
        slug: "mistral.magistral-small-2509",
        name: "Magistral Small",
        shortName: "Magistral Small",
        description: "Mistral Magistral reasoning model for chat and tools.",
        capabilities: ["reasoning", "tools"],
        context: "128K",
        tier: "pay",
      },
      {
        slug: "mistral.ministral-3-14b-instruct",
        name: "Ministral 3 14B",
        shortName: "Ministral 14B",
        description: "Compact Mistral Ministral for fast chat and tools.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "mistral.ministral-3-8b-instruct",
        name: "Ministral 3 8B",
        shortName: "Ministral 8B",
        description: "Small Mistral Ministral for fast chat and tools.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "mistral.ministral-3-3b-instruct",
        name: "Ministral 3 3B",
        shortName: "Ministral 3B",
        description: "Tiny Mistral Ministral for lightweight chat and tools.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
    ],
  },
  {
    instanceId: previewInstanceId("bedrock-zai"),
    driverKind: BEDROCK_KIND,
    displayName: "Z.AI",
    icon: ZaiGlyph,
    platform: "bedrock",
    kind: "chat",
    models: [
      {
        slug: "zai.glm-5",
        name: "GLM-5",
        shortName: "GLM-5",
        description: "Frontier Z.AI GLM for reasoning, coding and tools.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "pay",
      },
      {
        slug: "zai.glm-4.7",
        name: "GLM-4.7",
        shortName: "GLM-4.7",
        description: "Strong Z.AI GLM for reasoning, coding and tools.",
        capabilities: ["tools", "reasoning"],
        context: "128K",
        tier: "pay",
      },
      {
        slug: "zai.glm-4.7-flash",
        name: "GLM-4.7 Flash",
        shortName: "GLM-4.7 Flash",
        description: "Fast Z.AI GLM for lightweight chat and tools.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "zai.glm-4.6",
        name: "GLM-4.6",
        shortName: "GLM-4.6",
        description: "Open Z.AI GLM for chat, coding and tools.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
    ],
  },
  {
    instanceId: previewInstanceId("bedrock-moonshot"),
    driverKind: BEDROCK_KIND,
    displayName: "Moonshot AI",
    icon: MoonshotGlyph,
    platform: "bedrock",
    kind: "chat",
    models: [
      {
        slug: "moonshotai.kimi-k2-thinking",
        name: "Kimi K2 Thinking",
        shortName: "Kimi K2 Thinking",
        description: "Moonshot Kimi K2 reasoning model for agents and tools.",
        capabilities: ["reasoning", "tools"],
        context: "256K",
        tier: "pay",
      },
      {
        slug: "moonshotai.kimi-k2.5",
        name: "Kimi K2.5",
        shortName: "Kimi K2.5",
        description: "Moonshot Kimi K2.5 MoE for chat, coding and tools.",
        capabilities: ["tools"],
        context: "256K",
        tier: "pay",
      },
    ],
  },
  {
    instanceId: previewInstanceId("bedrock-minimax"),
    driverKind: BEDROCK_KIND,
    displayName: "MiniMax",
    icon: MiniMaxGlyph,
    platform: "bedrock",
    kind: "chat",
    models: [
      {
        slug: "minimax.minimax-m2.5",
        name: "MiniMax M2.5",
        shortName: "M2.5",
        description: "MiniMax M2.5 reasoning model for agents and tools.",
        capabilities: ["reasoning", "tools"],
        context: "128K",
        tier: "pay",
      },
      {
        slug: "minimax.minimax-m2.1",
        name: "MiniMax M2.1",
        shortName: "M2.1",
        description: "MiniMax M2.1 for chat, coding and tools.",
        capabilities: ["tools"],
        context: "128K",
        tier: "pay",
      },
      {
        slug: "minimax.minimax-m2",
        name: "MiniMax M2",
        shortName: "M2",
        description: "Open MiniMax M2 for chat, coding and tools.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
    ],
  },
  {
    instanceId: previewInstanceId("bedrock-nvidia"),
    driverKind: BEDROCK_KIND,
    displayName: "NVIDIA",
    icon: NvidiaGlyph,
    platform: "bedrock",
    kind: "chat",
    models: [
      {
        slug: "nvidia.nemotron-super-3-120b",
        name: "Nemotron Super 3 120B",
        shortName: "Nemotron Super 120B",
        description: "NVIDIA Nemotron Super reasoning model for tools.",
        capabilities: ["reasoning", "tools"],
        context: "128K",
        tier: "pay",
      },
      {
        slug: "nvidia.nemotron-nano-3-30b",
        name: "Nemotron Nano 3 30B",
        shortName: "Nemotron Nano 30B",
        description: "Compact NVIDIA Nemotron Nano for chat and tools.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "nvidia.nemotron-nano-12b-v2",
        name: "Nemotron Nano 12B",
        shortName: "Nemotron Nano 12B",
        description: "Small NVIDIA Nemotron Nano for fast chat and tools.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
      {
        slug: "nvidia.nemotron-nano-9b-v2",
        name: "Nemotron Nano 9B",
        shortName: "Nemotron Nano 9B",
        description: "Tiny NVIDIA Nemotron Nano for lightweight chat and tools.",
        capabilities: ["tools"],
        context: "128K",
        tier: "free",
      },
    ],
  },
  {
    instanceId: previewInstanceId("bedrock-writer"),
    driverKind: BEDROCK_KIND,
    displayName: "Writer",
    icon: WriterGlyph,
    platform: "bedrock",
    kind: "chat",
    models: [
      {
        slug: "writer.palmyra-vision-7b",
        name: "Palmyra Vision 7B",
        shortName: "Palmyra Vision 7B",
        description: "Writer Palmyra vision model for multimodal chat and tools.",
        capabilities: ["vision", "tools"],
        context: "32K",
        tier: "free",
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// UTILITY catalog — background models (speech / image / vision / embeddings).
// Grouped by CATEGORY. No user-facing tier; shown in a collapsed section.
// ---------------------------------------------------------------------------

function utilityGroup(
  slug: string,
  displayName: string,
  description: string,
  ids: ReadonlyArray<string>,
): PreviewCatalogGroup {
  return {
    instanceId: previewInstanceId(slug),
    driverKind: CF_KIND,
    displayName,
    icon: CloudflareGlyph,
    platform: "cloudflare",
    kind: "utility",
    models: ids.map((id) => ({
      slug: id,
      name: id.split("/").pop() ?? id,
      shortName: id.split("/").pop() ?? id,
      description,
      capabilities: [],
    })),
  };
}

export const UTILITY_GROUPS: ReadonlyArray<PreviewCatalogGroup> = [
  utilityGroup("util-stt", "Speech-to-Text", "Transcribes audio into text automatically.", [
    "@cf/openai/whisper",
    "@cf/openai/whisper-large-v3-turbo",
    "@cf/openai/whisper-tiny-en",
    "@cf/deepgram/nova-3",
    "@cf/deepgram/flux",
  ]),
  utilityGroup("util-tts", "Text-to-Speech", "Synthesizes spoken audio from text automatically.", [
    "@cf/deepgram/aura-1",
    "@cf/deepgram/aura-2-en",
    "@cf/deepgram/aura-2-es",
    "@cf/myshell-ai/melotts",
  ]),
  utilityGroup("util-t2i", "Text-to-Image", "Generates images from text prompts automatically.", [
    "@cf/black-forest-labs/flux-1-schnell",
    "@cf/black-forest-labs/flux-2-dev",
    "@cf/black-forest-labs/flux-2-klein-4b",
    "@cf/black-forest-labs/flux-2-klein-9b",
    "@cf/bytedance/stable-diffusion-xl-lightning",
    "@cf/leonardo/lucid-origin",
    "@cf/leonardo/phoenix-1.0",
    "@cf/stabilityai/stable-diffusion-xl-base-1.0",
  ]),
  utilityGroup("util-i2t", "Image-to-Text", "Describes and reasons over images automatically.", [
    "@cf/llava-hf/llava-1.5-7b-hf",
    "@cf/moondream/moondream3.1-9B-A2B",
  ]),
  utilityGroup(
    "util-embed",
    "Embeddings",
    "Vectorizes text for search and retrieval automatically.",
    [
      "@cf/baai/bge-m3",
      "@cf/baai/bge-large-en-v1.5",
      "@cf/baai/bge-base-en-v1.5",
      "@cf/qwen/qwen3-embedding-0.6b",
    ],
  ),
];

// Google Gemini on Vertex AI (EU multi-region), routed via the relay's
// `/google/` route. Gemini 3.1 Flash Lite is the platform default.
const VERTEX_CHAT_GROUPS: ReadonlyArray<PreviewCatalogGroup> = [
  {
    instanceId: previewInstanceId("vertex-google"),
    driverKind: BEDROCK_KIND,
    displayName: "Google",
    icon: Gemini,
    simpleIcon: "googlegemini",
    platform: "vertex",
    kind: "chat",
    models: [
      {
        slug: "google.gemini-3.1-flash-lite",
        name: "Gemini 3.1 Flash Lite",
        shortName: "Gemini 3.1 Flash Lite",
        description: "Google's fast, low-cost Gemini — the default model.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "1M",
        tier: "free",
      },
      {
        slug: "google.gemini-2.5-flash-lite",
        name: "Gemini 2.5 Flash Lite",
        shortName: "Gemini 2.5 Flash Lite",
        description: "Lightweight Gemini 2.5 — cheap and quick.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "1M",
        tier: "free",
      },
      {
        slug: "google.gemini-2.5-flash",
        name: "Gemini 2.5 Flash",
        shortName: "Gemini 2.5 Flash",
        description: "Solid all-round Gemini 2.5 for chat and tools.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "1M",
        tier: "pay",
      },
      {
        slug: "google.gemini-3.7-flash",
        name: "Gemini 3.7 Flash",
        shortName: "Gemini 3.7 Flash",
        description: "Google's top flash model — strongest reasoning. The default agent brain.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "1M",
        // Free tier: it's the default agent brain, so every plan can use it (kept in
        // sync with the server catalog's free-tier entry).
        tier: "free",
      },
      {
        slug: "google.gemini-2.5-pro",
        name: "Gemini 2.5 Pro",
        shortName: "Gemini 2.5 Pro",
        description: "Gemini 2.5 Pro for deep reasoning and long context.",
        capabilities: ["vision", "tools", "reasoning"],
        context: "1M",
        tier: "super",
      },
    ],
  },
];

/** Chat groups, grouped by company across all platforms. */
export const CHAT_GROUPS: ReadonlyArray<PreviewCatalogGroup> = [
  ...VERTEX_CHAT_GROUPS,
  ...CLOUDFLARE_CHAT_GROUPS,
  ...BEDROCK_CHAT_GROUPS,
];

// ---------------------------------------------------------------------------
// Company rail — one entry per COMPANY (deduped across platforms).
// ---------------------------------------------------------------------------

/**
 * A single provider company for the picker's left rail. A company may span
 * both Cloudflare and Bedrock (e.g. OpenAI, Mistral); its `groupInstanceIds`
 * list every catalog group that belongs to it so a rail click can filter the
 * main list to just that company's group(s).
 */
export type PreviewCompany = {
  /** Stable rail id derived from the display name (regex-safe, no colon). */
  id: string;
  /** Company display name (e.g. "Meta", "OpenAI", "Anthropic"). */
  displayName: string;
  /** Company brand / monogram icon (from the first group for this company). */
  icon: Icon;
  /** Simple Icons slug for the real brand logo (from the first group). */
  simpleIcon?: string | undefined;
  /** Instance ids of every catalog group belonging to this company. */
  groupInstanceIds: ReadonlyArray<ProviderInstanceId>;
};

/** Rail id prefix so a company selection never collides with a real instance id. */
export const PREVIEW_COMPANY_PREFIX = "morrow-company-";

export function isPreviewCompanyId(id: string): boolean {
  return id.startsWith(PREVIEW_COMPANY_PREFIX);
}

function companyIdFromDisplayName(displayName: string): string {
  // Keep it ProviderInstanceId-regex-safe: ^[a-zA-Z][a-zA-Z0-9_-]*$
  const slug = displayName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${PREVIEW_COMPANY_PREFIX}${slug || "company"}`;
}

/**
 * Companies for the rail, deduped by `displayName` and preserving catalog
 * order (all Cloudflare companies first, then Bedrock-only companies). A
 * company that appears on both platforms keeps its first-seen icon and
 * accumulates both groups' instance ids.
 */
export const PREVIEW_COMPANIES: ReadonlyArray<PreviewCompany> = (() => {
  const byName = new Map<string, PreviewCompany & { groupInstanceIds: ProviderInstanceId[] }>();
  const order: string[] = [];
  for (const group of CHAT_GROUPS) {
    const existing = byName.get(group.displayName);
    if (existing) {
      existing.groupInstanceIds.push(group.instanceId);
      continue;
    }
    order.push(group.displayName);
    byName.set(group.displayName, {
      id: companyIdFromDisplayName(group.displayName),
      displayName: group.displayName,
      icon: group.icon,
      simpleIcon: group.simpleIcon,
      groupInstanceIds: [group.instanceId],
    });
  }
  // MorrowOS: order the rail by fame — OpenAI, Claude, DeepSeek, Google, Meta (mid), then the rest.
  const FAME_ORDER = [
    "OpenAI",
    "Anthropic",
    "DeepSeek",
    "Google",
    "Meta",
    "Mistral",
    "Qwen",
    "Moonshot AI",
    "Amazon",
    "Z.AI",
    "NVIDIA",
    "IBM",
    "AI Singapore",
  ];
  const fameRank = (n: string) => {
    const i = FAME_ORDER.indexOf(n);
    return i === -1 ? FAME_ORDER.length : i;
  };
  order.sort((a, b) => fameRank(a) - fameRank(b));
  return order.map((name) => {
    const company = byName.get(name)!;
    return {
      id: company.id,
      displayName: company.displayName,
      icon: company.icon,
      simpleIcon: company.simpleIcon,
      groupInstanceIds: company.groupInstanceIds,
    };
  });
})();

/** Lookup: company id -> the set of catalog group instance ids it owns. */
export const PREVIEW_COMPANY_BY_ID: ReadonlyMap<string, PreviewCompany> = new Map(
  PREVIEW_COMPANIES.map((company) => [company.id, company] as const),
);

// ---------------------------------------------------------------------------
// Coming-soon providers — DISPLAY-ONLY rail entries.
//
// These render in the composer's left rail AFTER the active companies, as a
// long "coming soon" roster so the picker reads as 100+ providers. They carry
// a clean monogram logo, render DIMMED + NON-CLICKABLE, and do nothing on
// select (only active companies filter the model list). They have no catalog
// groups and never surface a model row.
// ---------------------------------------------------------------------------

/** Rail id prefix for coming-soon providers (regex-safe, never collides). */
export const COMING_SOON_PROVIDER_PREFIX = "morrow-soon-";

export function isComingSoonProviderId(id: string): boolean {
  return id.startsWith(COMING_SOON_PROVIDER_PREFIX);
}

/** A single "coming soon" provider for the rail. Purely presentational. */
export type ComingSoonProvider = {
  /** Stable rail id (regex-safe, e.g. "morrow-soon-xai"). */
  id: string;
  /** Provider display name. */
  displayName: string;
  /** Monogram brand icon (fallback when the real logo 404s). */
  icon: Icon;
  /** Simple Icons slug for the real brand logo (falls back to `icon`). */
  simpleIcon?: string | undefined;
};

/**
 * Roster spec: [display name, monogram label, brand-ish hex, rail slug,
 * Simple Icons slug?]. The icon is a monogram tile in the brand color (via
 * `makeMonogramIcon`, which colors the tile through a `text-[#hex]` class +
 * `currentColor` fill) and acts as the fallback when the real logo 404s. The
 * 5th element, when present, is the Simple Icons CDN slug for a real logo.
 */
const COMING_SOON_SPECS: ReadonlyArray<
  readonly [displayName: string, label: string, hex: string, slug: string, simpleIcon?: string]
> = [
  // xAI / X mark and Meta Muse must show real logos.
  ["xAI (Grok)", "xAI", "#000000", "xai", "x"],
  ["Meta Muse", "MU", "#0866ff", "meta-muse", "meta"],
  ["Perplexity", "PX", "#20808d", "perplexity", "perplexity"],
  ["Cohere", "CO", "#39594d", "cohere"],
  ["AI21 Labs", "A21", "#e8433f", "ai21"],
  ["Together AI", "TG", "#0f6fff", "together", "together"],
  ["Fireworks AI", "FW", "#5019c5", "fireworks"],
  ["Groq", "GQ", "#f55036", "groq"],
  ["Replicate", "RP", "#000000", "replicate", "replicate"],
  ["Hugging Face", "HF", "#ff9d00", "huggingface", "huggingface"],
  ["Stability AI", "ST", "#7c3aed", "stability", "stabilityai"],
  ["Reka", "RK", "#111827", "reka"],
  ["Inflection", "IN", "#1f6feb", "inflection"],
  ["MiniMax", "MM", "#ff4d4f", "minimax"],
  ["Baidu ERNIE", "BD", "#2932e1", "baidu-ernie", "baidu"],
  ["ByteDance Doubao", "DB", "#0052ff", "bytedance-doubao"],
  ["Tencent Hunyuan", "HY", "#0052d9", "tencent-hunyuan", "tencentqq"],
  ["01.AI Yi", "Yi", "#003366", "01ai-yi"],
  ["Cerebras", "CB", "#f05a28", "cerebras", "cerebras"],
  ["SambaNova", "SN", "#ee2a7b", "sambanova"],
  ["Writer", "WR", "#5a4be6", "writer"],
  ["Upstage", "UP", "#8b5cf6", "upstage"],
  ["Aleph Alpha", "AA", "#0a0a0a", "aleph-alpha"],
  ["LG EXAONE", "LG", "#a50034", "lg-exaone"],
  ["Databricks", "DBX", "#ff3621", "databricks", "databricks"],
  ["Snowflake Arctic", "SF", "#29b5e8", "snowflake-arctic", "snowflake"],
  ["Microsoft Phi", "Phi", "#0078d4", "microsoft-phi", "microsoft"],
  ["Nous Research", "NR", "#111827", "nous-research"],
  ["AllenAI", "AI2", "#f0529c", "allenai"],
  ["Voyage AI", "VY", "#1a56db", "voyage"],
  ["Jina AI", "JN", "#eb2f96", "jina"],
  ["Nomic", "NM", "#4f46e5", "nomic"],
  ["Contextual AI", "CX", "#0ea5e9", "contextual"],
  ["Character AI", "CH", "#2563eb", "character"],
  ["Poolside", "PS", "#111827", "poolside"],
  ["Magic", "MG", "#7c3aed", "magic"],
  ["Codeium", "CD", "#09b6a2", "codeium"],
  ["Tabnine", "TN", "#6f42c1", "tabnine"],
  ["Sourcegraph", "SG", "#ff5543", "sourcegraph", "sourcegraph"],
  ["Phind", "PH", "#4f46e5", "phind"],
  ["You.com", "YOU", "#7c3aed", "you-com"],
  ["Hume", "HM", "#ffb400", "hume"],
  ["ElevenLabs", "11", "#000000", "elevenlabs", "elevenlabs"],
  ["PlayHT", "PL", "#7c3aed", "playht"],
  ["Cartesia", "CT", "#111827", "cartesia"],
  ["Suno", "SU", "#000000", "suno", "suno"],
  ["Udio", "UD", "#111827", "udio"],
  ["Runway", "RW", "#000000", "runway", "runway"],
  ["Pika", "PK", "#ff5c8a", "pika"],
  ["Luma", "LM", "#111827", "luma"],
  ["Midjourney", "MJ", "#0a0a0a", "midjourney"],
  ["Ideogram", "ID", "#111827", "ideogram"],
  ["Recraft", "RC", "#111827", "recraft"],
  ["Krea", "KR", "#111827", "krea"],
  ["Kling", "KL", "#0052ff", "kling"],
  ["Hailuo", "HL", "#ff4d4f", "hailuo"],
  ["Vidu", "VD", "#6d28d9", "vidu"],
  ["Genmo", "GM", "#111827", "genmo"],
  ["Assembly AI", "AS", "#3d5afe", "assemblyai"],
  ["Speechmatics", "SM", "#00c2b2", "speechmatics"],
  ["Gladia", "GL", "#6d28d9", "gladia"],
  ["Fal", "FAL", "#e11d48", "fal"],
  ["Modal", "MD", "#7fee64", "modal", "modal"],
  ["Baseten", "BT", "#111827", "baseten"],
  ["Anyscale", "AN", "#0b6efd", "anyscale"],
  ["OctoAI", "OC", "#0f172a", "octoai"],
  ["Lepton", "LP", "#111827", "lepton"],
  ["Novita", "NV", "#22c55e", "novita"],
  ["DeepInfra", "DI", "#5b21b6", "deepinfra"],
  ["Hyperbolic", "HB", "#111827", "hyperbolic"],
  ["Lambda", "LB", "#4629dd", "lambda"],
  ["CoreWeave", "CW", "#0a0a0a", "coreweave"],
  ["RunPod", "RD", "#673ab7", "runpod"],
  ["Predibase", "PB", "#111827", "predibase"],
  ["Lamini", "LN", "#111827", "lamini"],
  ["Perplexity Sonar", "SO", "#20808d", "perplexity-sonar", "perplexity"],
  ["Liquid AI", "LQ", "#0ea5e9", "liquid"],
  ["Sarvam", "SV", "#f97316", "sarvam"],
  ["Krutrim", "KT", "#111827", "krutrim"],
  ["Falcon (TII)", "FC", "#009fe3", "falcon-tii"],
  ["OpenRouter", "OR", "#6467f2", "openrouter", "openrouter"],
  ["Portkey", "PT", "#111827", "portkey"],
  ["LiteLLM", "LL", "#22c55e", "litellm"],
];

/** The coming-soon roster, rendered dimmed + non-clickable after active companies. */
export const COMING_SOON_PROVIDERS: ReadonlyArray<ComingSoonProvider> = COMING_SOON_SPECS.map(
  ([displayName, label, hex, slug, simpleIcon]) => ({
    id: `${COMING_SOON_PROVIDER_PREFIX}${slug}`,
    displayName,
    // Brand color is a runtime value, so drive it via inline `color`
    // (Tailwind's JIT can't see dynamically-built class strings). This monogram
    // is also the fallback when the real Simple Icons logo 404s.
    icon: makeMonogramIcon(label, "", { color: hex }),
    simpleIcon,
  }),
);

/**
 * Combined rail roster: active companies (comingSoon:false) first, then the
 * coming-soon providers (comingSoon:true). Consumed by the picker sidebar.
 */
export type RailProvider = {
  id: string;
  displayName: string;
  icon: Icon;
  /** Simple Icons slug for the real brand logo (falls back to `icon`). */
  simpleIcon?: string | undefined;
  comingSoon: boolean;
};

export const RAIL_PROVIDERS: ReadonlyArray<RailProvider> = [
  ...PREVIEW_COMPANIES.map((company) => ({
    id: company.id,
    displayName: company.displayName,
    icon: company.icon,
    simpleIcon: company.simpleIcon,
    comingSoon: false as const,
  })),
  ...COMING_SOON_PROVIDERS.map((provider) => ({
    id: provider.id,
    displayName: provider.displayName,
    icon: provider.icon,
    simpleIcon: provider.simpleIcon,
    comingSoon: true as const,
  })),
];

/**
 * Full catalog (chat + utility). Retained under the historical name for any
 * consumers that iterate over every preview group.
 */
export const MORROW_PREVIEW_CATALOG: ReadonlyArray<PreviewCatalogGroup> = [
  ...CHAT_GROUPS,
  ...UTILITY_GROUPS,
];

/** Every catalog model indexed by its bare provider slug (e.g. `deepseek.v3.2`). */
const MORROW_MODEL_BY_SLUG: ReadonlyMap<string, PreviewCatalogModel> = new Map(
  MORROW_PREVIEW_CATALOG.flatMap((group) =>
    group.models.map((model) => [model.slug, model] as const),
  ),
);

/**
 * Resolve a bare provider model slug to its catalog entry, so callers can show
 * a friendly name ("DeepSeek V3.2") in place of the raw slug — even before the
 * live provider snapshot has streamed its model options in (draft/hero state).
 */
export function lookupMorrowModel(slug: string): PreviewCatalogModel | undefined {
  return MORROW_MODEL_BY_SLUG.get(slug);
}

/** Every catalog model's owning company group, indexed by bare slug. */
const MORROW_GROUP_BY_SLUG: ReadonlyMap<string, PreviewCatalogGroup> = new Map(
  MORROW_PREVIEW_CATALOG.flatMap((group) =>
    group.models.map((model) => [model.slug, group] as const),
  ),
);

export interface MorrowModelBrand {
  readonly displayName: string;
  readonly icon: Icon;
  readonly simpleIcon: string | undefined;
}

/**
 * The company brand (logo + name) for a model slug — so the composer trigger can
 * show the selected model's real brand mark (OpenAI, Meta, DeepSeek, …) instead
 * of the generic agent icon. Accepts native-agent slugs (`morrow-agent:<id>`).
 */
export function lookupMorrowModelBrand(slug: string): MorrowModelBrand | undefined {
  const bare = slug.startsWith("morrow-agent:") ? slug.slice("morrow-agent:".length) : slug;
  const group = MORROW_GROUP_BY_SLUG.get(bare);
  if (!group) {
    return undefined;
  }
  return { displayName: group.displayName, icon: group.icon, simpleIcon: group.simpleIcon };
}
